import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const StageCreateModal = ({ apiUrl, open, onClose, onCreated }) => {
  const [mode, setMode] = useState('job'); // 'job' | 'service'
  const [form, setForm] = useState({
    job_number: '',
    service_log_number: '',
    stage_date: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleModeChange = (e) => {
    const val = e.target.value;
    setMode(val);
    // Clear the other field to avoid sending both
    setForm(prev => ({
      ...prev,
      job_number: val === 'job' ? prev.job_number : '',
      service_log_number: val === 'service' ? prev.service_log_number : '',
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // inside StageCreateModal.jsx
  const submit = async (e) => {
    e.preventDefault();

    if (mode === 'job' && !form.job_number.trim()) {
      toast.warn('Enter a Job #');
      return;
    }
    if (mode === 'service' && !form.service_log_number.trim()) {
      toast.warn('Enter a Service Log #');
      return;
    }

    const payload = {
      job_number: mode === 'job' ? form.job_number.trim() : null,
      service_log_number: mode === 'service' ? form.service_log_number.trim() : null,
      stage_date: form.stage_date,
      notes: form.notes,
    };

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${apiUrl}/inventory_crud.php`,
        { status: 'addStaging', data: payload },
        { headers: { 'Content-Type': 'application/json' } }
      );

      // expect: { success: true, stage_id } OR ['success', { stage_id }]
      const ok = (res.data && res.data.success === true)
        || (Array.isArray(res.data) && res.data[0] === 'success');

      if (ok) {
        const stageId = res.data.stage_id
          || (Array.isArray(res.data) && res.data[1]?.stage_id);
        toast.success('Stage created successfully');
        onCreated?.(stageId || null);
        onClose();
      } else {
        toast.error('Failed to create stage');
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        // optional: if you have access to navigate here, you could redirect
        toast.error('Session expired. Please login again.', { position: 'top-center' });
      } else {
        toast.error('API error creating stage');
      }
    } finally {
      setSubmitting(false);
    }
  };


  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-xl rounded-lg shadow-lg">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">New Stage</h2>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
            type="button"
          >
            ✕
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {/* Radio selector */}
          <fieldset className="border rounded p-3">
            <legend className="text-sm font-medium">Identify by</legend>
            <div className="flex items-center gap-6 mt-2">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="mode"
                  value="job"
                  checked={mode === 'job'}
                  onChange={handleModeChange}
                />
                <span>Job #</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="mode"
                  value="service"
                  checked={mode === 'service'}
                  onChange={handleModeChange}
                />
                <span>Service Log #</span>
              </label>
            </div>
          </fieldset>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {mode === 'job' && (
              <div className="md:col-span-1">
                <label className="block text-sm font-medium mb-1">Job #</label>
                <input
                  name="job_number"
                  value={form.job_number}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g. J-2025-123"
                  autoFocus
                />
              </div>
            )}
            {mode === 'service' && (
              <div className="md:col-span-1">
                <label className="block text-sm font-medium mb-1">Service Log #</label>
                <input
                  name="service_log_number"
                  value={form.service_log_number}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                  placeholder="e.g. SVC-8891"
                  autoFocus
                />
              </div>
            )}

            <div className="md:col-span-1">
              <label className="block text-sm font-medium mb-1">Stage Date</label>
              <input
                type="date"
                name="stage_date"
                value={form.stage_date}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              rows={3}
              placeholder="Optional notes…"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              className="px-4 py-2 rounded border"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded disabled:opacity-60"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'Create Stage'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StageCreateModal;
